import { lazyWithRecovery } from "../../lib/chunk-load-recovery/lazy-with-recovery";
import { type RouteObject } from "react-router-dom";

import { accountUpdateLoader } from "../../app/router/guards/loaders/account-update.loader";
import DefaultLayout from "../../components/layouts/default-layout/DefaultLayout";
import RouteWrapper from "../../components/layouts/route-wrapper/RouteWrapper";
import {
  PROFILE_ACTIVITY_DATA_VIEW_PATH,
  PROFILE_HOUSEHOLD_DATA_VIEW_PATH,
  PROFILE_HOUSEHOLD_MARITAL_STATUS_PATH,
  PROFILE_HOUSEHOLD_MEMBER_DATA_CREATE_PATH,
  PROFILE_HOUSEHOLD_MEMBER_DATA_EDIT_PATH,
  PROFILE_HOUSEHOLD_MEMBER_DATA_EDIT_PLACEHOLDER_PATH,
  PROFILE_HOUSEHOLD_MEMBER_PERSONAL_DATA_EDIT_PATH,
  PROFILE_HOUSEHOLD_MEMBER_RELATIONSHIP_EDIT_PATH,
  PROFILE_INCOME_DATA_EDIT_PATH,
  PROFILE_INCOME_DATA_VIEW_PATH,
  PROFILE_ISEE_DATA_VIEW_PATH,
  PROFILE_PATH,
  PROFILE_PERSONAL_DATA_EDIT_PATH,
  PROFILE_PERSONAL_DATA_VIEW_PATH,
  PROFILE_REAL_ESTATE_DATA_EDIT_PATH,
  PROFILE_REAL_ESTATE_DATA_VIEW_PATH,
} from "../../constants/env";
import { RouteWrapperVariant } from "../../constants/layouts/route-wrapper-variant.enum";
import { ConfirmExitEditHouseholdMemberPersonalDataModal } from "../../features/profile/components/ConfirmExitEditHouseholdMemberPersonalDataModal/ConfirmExitEditHouseholdMemberPersonalDataModal";
import { ConfirmExitMaritalStatusModal } from "../../features/profile/components/ConfirmExitMaritalStatusModal/ConfirmExitMaritalStatusModal";
import CloseChatbotPanelWrapper from "../../legacy/components/Chatbot/wrappers/close-chatbot-panel-wrapper/close-chatbot-panel-wrapper";
import { PaymentBackdrop } from "../../legacy/components/PaymentBackdrop/payment-backdrop";
import SEO from "../../legacy/components/Seo";
import { ConfirmExitContent } from "../../legacy/pages/ProfileData/modals/confirm-exit-content/confirm-exit-content";
import { ConfirmExitCreateHouseholdMemberModal } from "../../legacy/pages/ProfileData/UpdateProductRequisites/UpdateProductRequisitesHouseholdPage/modals/confirm-exit-create-household-member.modal";
import { ConfirmExitEditHouseholdMemberRelationshipModal } from "../../legacy/pages/ProfileData/UpdateProductRequisites/UpdateProductRequisitesHouseholdPage/modals/confirm-exit-edit-household-member-relationship.modal";
import { withSuspense } from "./utils/with-suspense";

const PersonalDataPage = lazyWithRecovery(
  () => import("../../legacy/pages/ProfileData/PersonalDataPage/PersonalDataPage")
);
const EditPersonalDataPage = lazyWithRecovery(
  () => import("../../app/pages/profile/EditPersonalDataPage/EditPersonalDataPage")
);
const ActivityDataPage = lazyWithRecovery(
  () => import("../../legacy/pages/ProfileData/ActivityDataPage/ActivityDataPage")
);
const HouseholdPage = lazyWithRecovery(() => import("../../legacy/pages/ProfileData/HouseholdPage/HouseholdPage"));
const IseePage = lazyWithRecovery(() => import("../../legacy/pages/ProfileData/IseePage/IseePage"));
const HouseholdMemberCreatePage = lazyWithRecovery(
  () => import("../../legacy/pages/ProfileData/HouseholdMemberCreatePage/HouseholdMemberCreatePage")
);
const HouseholdMemberEditPlaceholderPage = lazyWithRecovery(
  () => import("../../app/pages/profile/HouseholdMemberEditPlaceholderPage/HouseholdMemberEditPlaceholderPage")
);
const MaritalStatusPage = lazyWithRecovery(() => import("../../app/pages/profile/MaritalStatusPage/MaritalStatusPage"));
const HouseholdMemberEditPage = lazyWithRecovery(
  () => import("../../legacy/pages/ProfileData/HouseholdMemberEditPage/HouseholdMemberEditPage")
);
const HouseholdMemberEditPersonalDataPage = lazyWithRecovery(
  () => import("../../legacy/pages/ProfileData/HouseholdMemberEditPage/HouseholdMemberEditPersonalDataPage")
);
const HouseholdMemberEditRelationshipPage = lazyWithRecovery(
  () => import("../../legacy/pages/ProfileData/HouseholdMemberEditPage/HouseholdMemberEditRelationshipPage")
);
const RealEstatePageRoute = lazyWithRecovery(
  () => import("../../app/pages/profile/RealEstatePage/RealEstatePageRoute")
);
const RealEstateEditPage = lazyWithRecovery(
  () => import("../../app/pages/profile/RealEstateEditPage/RealEstateEditPage")
);
const IncomePage = lazyWithRecovery(() => import("../../app/pages/profile/IncomePage/IncomePage"));
const IncomeEditPage = lazyWithRecovery(() => import("../../app/pages/profile/IncomeEditPage/IncomeEditPage"));
const ProfilePage = lazyWithRecovery(() => import("../../app/pages/profile/ProfilePage/ProfilePage"));

export function createProfileRoutes(): RouteObject[] {
  return [
    {
      path: PROFILE_PERSONAL_DATA_EDIT_PATH,
      element: (
        <DefaultLayout variant="white">
          <SEO title="Modifica dati personali" />
          <RouteWrapper
            variant={RouteWrapperVariant.fullScreenWithHeaderAndCloseModal}
            title="Su di te"
            defaultCloseButtonModal={<ConfirmExitContent />}
          >
            <CloseChatbotPanelWrapper>{withSuspense(<EditPersonalDataPage />)}</CloseChatbotPanelWrapper>
          </RouteWrapper>
        </DefaultLayout>
      ),
    },
    {
      path: PROFILE_HOUSEHOLD_MEMBER_DATA_CREATE_PATH,
      element: (
        <DefaultLayout variant="white">
          <SEO title="Aggiungi familiare" />
          <RouteWrapper
            variant={RouteWrapperVariant.fullScreenWithHeaderAndCloseModal}
            title="Aggiungi familiare"
            defaultCloseButtonModal={<ConfirmExitCreateHouseholdMemberModal />}
            progressBar={true}
            progressBarPercentage={0}
          >
            <CloseChatbotPanelWrapper>{withSuspense(<HouseholdMemberCreatePage />)}</CloseChatbotPanelWrapper>
          </RouteWrapper>
        </DefaultLayout>
      ),
    },
    {
      path: PROFILE_HOUSEHOLD_MEMBER_DATA_EDIT_PLACEHOLDER_PATH,
      element: (
        <DefaultLayout variant="white">
          <SEO title="Aggiungi familiare" />
          <RouteWrapper
            variant={RouteWrapperVariant.fullScreenWithHeaderAndCloseModal}
            title="Aggiungi familiare"
            defaultCloseButtonModal={<ConfirmExitCreateHouseholdMemberModal />}
            progressBar={true}
            progressBarPercentage={0}
          >
            <CloseChatbotPanelWrapper>{withSuspense(<HouseholdMemberEditPlaceholderPage />)}</CloseChatbotPanelWrapper>
          </RouteWrapper>
        </DefaultLayout>
      ),
    },
    {
      path: PROFILE_HOUSEHOLD_MARITAL_STATUS_PATH,
      element: (
        <DefaultLayout variant="white">
          <SEO title="Stato civile" />
          <RouteWrapper
            variant={RouteWrapperVariant.fullScreenWithHeaderAndCloseModal}
            title="Nucleo familiare"
            defaultCloseButtonModal={<ConfirmExitMaritalStatusModal />}
          >
            <CloseChatbotPanelWrapper>{withSuspense(<MaritalStatusPage />)}</CloseChatbotPanelWrapper>
          </RouteWrapper>
        </DefaultLayout>
      ),
    },
    {
      path: PROFILE_HOUSEHOLD_MEMBER_PERSONAL_DATA_EDIT_PATH,
      element: (
        <DefaultLayout variant="white">
          <SEO title="Modifica dati personali" />
          <RouteWrapper
            variant={RouteWrapperVariant.fullScreenWithHeaderAndCloseModal}
            defaultCloseButtonModal={<ConfirmExitEditHouseholdMemberPersonalDataModal />}
          >
            <CloseChatbotPanelWrapper>{withSuspense(<HouseholdMemberEditPersonalDataPage />)}</CloseChatbotPanelWrapper>
          </RouteWrapper>
        </DefaultLayout>
      ),
    },
    {
      path: PROFILE_HOUSEHOLD_MEMBER_RELATIONSHIP_EDIT_PATH,
      element: (
        <DefaultLayout variant="white">
          <SEO title="Modifica relazione" />
          <RouteWrapper
            variant={RouteWrapperVariant.fullScreenWithHeaderAndCloseModal}
            defaultCloseButtonModal={<ConfirmExitEditHouseholdMemberRelationshipModal />}
          >
            <CloseChatbotPanelWrapper>{withSuspense(<HouseholdMemberEditRelationshipPage />)}</CloseChatbotPanelWrapper>
          </RouteWrapper>
        </DefaultLayout>
      ),
    },
  ];
}

export function createProfileRoutesDefaultLayoutOutlet(): RouteObject[] {
  return [
    {
      path: PROFILE_HOUSEHOLD_MEMBER_DATA_EDIT_PATH,
      element: (
        <>
          <SEO title="Modifica familiare" />
          <RouteWrapper
            variant={RouteWrapperVariant.profileData}
            defaultBackButtonUrl={PROFILE_HOUSEHOLD_DATA_VIEW_PATH}
            defaultCloseButtonModal={<ConfirmExitContent />}
          >
            <CloseChatbotPanelWrapper>{withSuspense(<HouseholdMemberEditPage />)}</CloseChatbotPanelWrapper>
          </RouteWrapper>
        </>
      ),
    },
    {
      path: PROFILE_PERSONAL_DATA_VIEW_PATH,
      element: (
        <>
          <SEO title="Dati personali" />
          <RouteWrapper
            variant={RouteWrapperVariant.profileData}
            defaultBackButtonUrl={PROFILE_PATH}
            title="Dati personali"
          >
            {withSuspense(<PersonalDataPage />)}
          </RouteWrapper>
        </>
      ),
    },
    {
      path: PROFILE_ACTIVITY_DATA_VIEW_PATH,
      element: (
        <>
          <SEO title="Attività" />
          <RouteWrapper variant={RouteWrapperVariant.profileData} defaultBackButtonUrl={PROFILE_PATH} title="Attività">
            {withSuspense(<ActivityDataPage />)}
          </RouteWrapper>
        </>
      ),
    },
    {
      path: PROFILE_HOUSEHOLD_DATA_VIEW_PATH,
      element: (
        <>
          <SEO title="Nucleo familiare" />
          <RouteWrapper
            variant={RouteWrapperVariant.profileData}
            defaultBackButtonUrl={PROFILE_PATH}
            title="Nucleo familiare"
          >
            {withSuspense(<HouseholdPage />)}
          </RouteWrapper>
        </>
      ),
    },
    {
      path: PROFILE_REAL_ESTATE_DATA_EDIT_PATH,
      element: (
        <>
          <SEO title="Modifica casa" />
          <RouteWrapper
            variant={RouteWrapperVariant.profileData}
            defaultBackButtonUrl={PROFILE_REAL_ESTATE_DATA_VIEW_PATH}
            title="Casa"
          >
            {withSuspense(<RealEstateEditPage />)}
          </RouteWrapper>
        </>
      ),
    },
    {
      path: PROFILE_REAL_ESTATE_DATA_VIEW_PATH,
      element: withSuspense(<RealEstatePageRoute />),
    },
    {
      path: PROFILE_ISEE_DATA_VIEW_PATH,
      element: (
        <>
          <SEO title="ISEE" />
          <RouteWrapper variant={RouteWrapperVariant.profileData} defaultBackButtonUrl={PROFILE_PATH} title="ISEE">
            {withSuspense(<IseePage />)}
          </RouteWrapper>
        </>
      ),
    },
    {
      path: PROFILE_INCOME_DATA_VIEW_PATH,
      element: (
        <>
          <SEO title="Redditi" />
          <RouteWrapper variant={RouteWrapperVariant.profileData} defaultBackButtonUrl={PROFILE_PATH} title="Redditi">
            {withSuspense(<IncomePage />)}
          </RouteWrapper>
        </>
      ),
    },
    {
      path: PROFILE_INCOME_DATA_EDIT_PATH,
      element: (
        <>
          <SEO title="Modifica reddito" />
          <RouteWrapper
            variant={RouteWrapperVariant.profileData}
            defaultBackButtonUrl={PROFILE_INCOME_DATA_VIEW_PATH}
            title="Redditi"
          >
            {withSuspense(<IncomeEditPage />)}
          </RouteWrapper>
        </>
      ),
    },
    {
      path: `${PROFILE_PATH}/*`,
      loader: accountUpdateLoader,
      element: (
        <>
          <SEO title="Profilo" />
          <PaymentBackdrop>
            <RouteWrapper variant={RouteWrapperVariant.profile}>{withSuspense(<ProfilePage />)}</RouteWrapper>
          </PaymentBackdrop>
        </>
      ),
    },
  ];
}
